import React, { useCallback } from "react";
import "./lesson-card.component.scoped.scss";
import { Box, LinearProgress, Typography } from "@material-ui/core";
import { educationRoutes } from "../../types/education.routes";
import { history } from "react-router-guard";
import { Skeleton } from "@material-ui/lab";
import { icons } from "../../../../statics/icons";
import ActionMenuComponent from "../../../reusable-components/components/menu.component";
import { useTranslation } from "react-i18next";
import { formatDate, formatTime } from "../../../../helpers";
import { LessonStatus } from "../../types/lesson.types";

const LessonCardComponent = ({
  lesson,
  loading,
  hasFullAccess = false,
  sectionLocked = false,
  onEdit,
  onDelete,
}: {
  lesson: any;
  loading: boolean;
  hasFullAccess?: boolean;
  sectionLocked?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}) => {
  const { t } = useTranslation();

  const canOpenLesson = useCallback(
    (lesson: any): boolean => {
      if (!lesson) return false;
      if (hasFullAccess) return true;
      return lesson.status === "ACTIVE" && !lesson.locked && !sectionLocked;
    },
    [hasFullAccess, sectionLocked],
  );

  const handleLessonClick = useCallback(
    (lesson: any) => {
      if (!lesson?.id || !canOpenLesson(lesson)) return;
      history.push(educationRoutes.lesson(lesson.id));
    },
    [canOpenLesson],
  );

  return (
    <Box className="lesson__card">
      <Box
        className="lesson__image"
        style={{
          cursor: canOpenLesson(lesson) ? "pointer" : "default",
        }}
        onClick={() => handleLessonClick(lesson)}
      >
        {loading ? (
          <Skeleton variant="rect" height="15rem" width="100%" />
        ) : (
          <img src={lesson.imageURL} alt={lesson.title} />
        )}
        {!loading &&
        lesson &&
        (lesson.userStatus?.status === LessonStatus.COMPLETED ||
          lesson.status === LessonStatus.INACTIVE) ? (
          <Box className="lesson__image__overlay">
            <Box
              className="lesson__image__overlay__icon"
              style={{
                background:
                  lesson.status === LessonStatus.INACTIVE
                    ? "#071437"
                    : "#aacd34",
              }}
            >
              <img
                src={
                  lesson.status === LessonStatus.INACTIVE
                    ? icons.LOCK
                    : icons.PAGES.EDUCATION.COMPLETED
                }
                alt={
                  lesson.status === LessonStatus.INACTIVE ? "Lock" : "Completed"
                }
              />
            </Box>
          </Box>
        ) : null}
      </Box>
      <Box className="lesson__info p-3">
        <Box className="d-flex align-items-center justify-content-between mb-2">
          {loading ? (
            <Skeleton
              variant="text"
              height={20}
              width="80%"
              style={{ marginBottom: "10px" }}
            />
          ) : (
            <Typography
              variant="h3"
              className="font-weight-bold d-flex"
              style={{
                gap: "0.5rem",
                cursor: canOpenLesson(lesson) ? "pointer" : "default",
              }}
              onClick={() => handleLessonClick(lesson)}
            >
              {!hasFullAccess && (lesson.locked || sectionLocked) && (
                <img
                  src={icons.LOCK_BLACK}
                  alt="lesson"
                  width={20}
                  height={20}
                />
              )}
              {lesson.title}
            </Typography>
          )}

          {hasFullAccess && (
            <Box className="actions">
              <ActionMenuComponent
                key={lesson.id}
                items={[
                  {
                    label: t("EDIT"),
                    onClick: () => {
                      onEdit?.();
                    },
                  },
                  {
                    label: t("DELETE"),
                    onClick: () => {
                      onDelete?.();
                    },
                  },
                ]}
              />
            </Box>
          )}
        </Box>
        <Box
          className="d-flex align-items-center justify-content-between mb-4"
          style={{ gap: ".5rem" }}
        >
          {loading ? (
            <Skeleton variant="text" height={20} width="40%" />
          ) : (
            <Typography
              variant="caption"
              color="textSecondary"
              className="d-flex align-items-center"
              style={{ gap: "0.5rem" }}
            >
              <img src={icons.CALENDER} alt="calender" />{" "}
              {formatDate(lesson.createdAt, "ddd, MMM DD, HH:mm Z")}
            </Typography>
          )}
          {loading ? (
            <Skeleton variant="text" height={20} width="40%" />
          ) : (
            <Typography
              variant="caption"
              color="textSecondary"
              className={`d-flex align-items-center
                ${
                  !hasFullAccess &&
                  (lesson.status === LessonStatus.INACTIVE ||
                    lesson.locked ||
                    sectionLocked)
                    ? "inactive__lesson"
                    : ""
                } ${
                !hasFullAccess &&
                lesson.userStatus?.status === LessonStatus.NOT_STARTED
                  ? "new__lesson"
                  : ""
              }`}
              style={{ gap: "0.5rem" }}
              onClick={() => {
                if (
                  !hasFullAccess &&
                  lesson.userStatus?.status === LessonStatus.NOT_STARTED &&
                  !(lesson.locked || sectionLocked)
                ) {
                  handleLessonClick(lesson);
                }
              }}
            >
              <img src={icons.clock} alt="clock" />{" "}
              {!hasFullAccess &&
              lesson.userStatus?.status === LessonStatus.NOT_STARTED ? (
                <span>
                  {t("NEW")}: {t("CLICK_TO_START")}
                </span>
              ) : (
                <span>
                  {t("EDUCATION_PAGE.DURATION")}: {formatTime(lesson.duration)}{" "}
                  Min
                </span>
              )}
            </Typography>
          )}
        </Box>
        {!hasFullAccess && (
          <Box className="status__bar">
            {loading ? (
              <Skeleton variant="text" height={8} width="100%" />
            ) : (
              <Box
                style={{
                  position: "relative",
                  width: "100%",
                  height: "8px",
                  backgroundColor: "var(--light)",
                  borderRadius: "12px",
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={
                    lesson.userStatus?.status === LessonStatus.COMPLETED
                      ? 100
                      : ((lesson.userStatus?.progressSeconds ?? 0) /
                          lesson.duration) *
                        100
                  }
                  className={
                    lesson.userStatus?.status === LessonStatus.COMPLETED
                      ? "completed__progress"
                      : ""
                  }
                  style={{
                    height: "8px",
                    borderRadius: "12px",
                    backgroundColor: "transparent",
                  }}
                />
              </Box>
            )}
            {lesson.userStatus?.status === LessonStatus.NOT_STARTED && (
              <Typography
                variant="caption"
                color="textSecondary"
                className="d-flex"
              >
                {loading ? (
                  <Skeleton variant="text" height={14} width="200px" />
                ) : (
                  <span>{t("EDUCATION_PAGE.YOUR_PROGRESS_NOT_STARTED")}</span>
                )}
              </Typography>
            )}

            {lesson.userStatus?.status !== LessonStatus.NOT_STARTED && (
              <Box className="d-flex align-items-center justify-content-between">
                {loading ? (
                  <Skeleton variant="text" height={14} width="50px" />
                ) : (
                  <Typography variant="caption" color="textSecondary">
                    {lesson.userStatus?.status === LessonStatus.COMPLETED
                      ? formatTime(lesson.duration)
                      : formatTime(
                          lesson.userStatus?.progressSeconds ?? 0,
                        )}{" "}
                    Min
                  </Typography>
                )}
                {loading ? (
                  <Skeleton variant="text" height={14} width="50px" />
                ) : (
                  <Typography variant="caption" color="textSecondary">
                    {formatTime(lesson.duration)} Min
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LessonCardComponent;
